import { motion } from "framer-motion";
import { Check, Terminal } from "lucide-react";

const CODE_LINES = [
  { t: [{ c: "muted", v: "// Geschäftskonto in Sekunden erstellen" }] },
  { t: [{ c: "kw", v: "const" }, { c: "plain", v: " account = " }, { c: "kw", v: "await" }, { c: "plain", v: " cashfux.accounts." }, { c: "fn", v: "create" }, { c: "plain", v: "({" }] },
  { t: [{ c: "plain", v: "  type: " }, { c: "str", v: '"business"' }, { c: "plain", v: "," }] },
  { t: [{ c: "plain", v: "  currency: " }, { c: "str", v: '"EUR"' }, { c: "plain", v: "," }] },
  { t: [{ c: "plain", v: "  entity: " }, { c: "str", v: '"acme-gmbh"' }, { c: "plain", v: "," }] },
  { t: [{ c: "plain", v: "});" }] },
  { t: [{ c: "plain", v: "" }] },
  { t: [{ c: "muted", v: "// → IBAN, Ledger & Webhook aktiv" }] },
  { t: [{ c: "plain", v: "account.iban " }, { c: "acc", v: "// DE89 3704 0044 0532 0130 00" }] },
];

const COLORS = {
  plain: "text-white/80",
  kw: "text-[#e01e37]",
  str: "text-emerald-400/90",
  fn: "text-sky-400/90",
  muted: "text-white/30",
  acc: "text-white/40",
};

const BENEFITS = [
  "REST-first mit vollständiger OpenAPI-Spezifikation",
  "Ereignisgetriebene Webhooks & idempotente Requests",
  "Sandbox-Umgebung mit realistischen Testdaten",
  "SDKs für TypeScript, Python und Go",
];

export default function ApiSection() {
  return (
    <section id="api" className="relative border-t border-white/10 bg-[#05060a] py-16 sm:py-24 lg:py-32" data-testid="api-section">
      <div className="absolute inset-0 grid-bg-fine opacity-30" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-3">
              <span className="h-px w-8 bg-[#e01e37]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#e01e37]">
                Developer API
              </span>
            </div>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.02]">
              Integration in Stunden, nicht Monaten.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-white/55 leading-relaxed">
              Jede Bankfunktion als saubere, dokumentierte Schnittstelle. Vom
              ersten API-Call bis zum produktiven Zahlungsfluss ohne Reibung.
            </p>

            <ul className="mt-10 space-y-4">
              {BENEFITS.map((b, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-[#e01e37]/40 text-[#e01e37]">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-sm lg:text-base text-white/70">{b}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Right: code block */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="border border-white/10 bg-[#0a0c12] accent-glow">
              {/* terminal header */}
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
                <div className="flex items-center gap-2 text-white/40">
                  <Terminal className="h-3.5 w-3.5" />
                  <span className="font-mono text-xs">cashfux.ts</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 bg-white/15" />
                  <span className="h-2.5 w-2.5 bg-white/15" />
                  <span className="h-2.5 w-2.5 bg-[#e01e37]/70" />
                </div>
              </div>
              {/* code */}
              <div className="overflow-x-auto p-6 lg:p-8">
                <pre className="font-mono text-xs sm:text-sm leading-relaxed">
                  <code>
                    {CODE_LINES.map((line, i) => (
                      <div key={i} className="flex">
                        <span className="mr-5 select-none text-white/15 w-4 text-right">{i + 1}</span>
                        <span>
                          {line.t.map((tok, j) => (
                            <span key={j} className={COLORS[tok.c]}>
                              {tok.v}
                            </span>
                          ))}
                        </span>
                      </div>
                    ))}
                  </code>
                </pre>
              </div>
              {/* status bar */}
              <div className="flex items-center justify-between border-t border-white/10 px-5 py-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
                  POST /v1/accounts
                </span>
                <span className="font-mono text-[10px] text-emerald-400/80">200 OK · 84ms</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
